import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Stack,
  Typography,
  Link,
  Sheet,
  CircularProgress,
  styled,
  Button,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  FormControl,
  PinInput,
  FormHelperText,
} from "@financeable-com-au/financeable-ui";
import { useFormik } from "formik";
import * as yup from "yup";
import { AuthCard } from "@components/AuthCard";
import {
  AUTH_REQUEST_KEYS,
  authV2InvitationMfaEnrol,
  authV2InvitationMfaVerify,
  authV2Selector,
  toAuthV2Error,
  type MfaEnrolmentData,
} from "@/store/slices/authV2Slice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import type { AppDispatch } from "@/store/store";

const QRCodeWrapper = styled(Sheet)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
`;

const QRCodeImage = styled("img")`
  width: 100%;
  height: 100%;
`;

const AuthInviteSetMfa = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [enrolment, setEnrolment] = useState<MfaEnrolmentData | null>(null);
  const [showSetupKey, setShowSetupKey] = useState(false);
  const authV2 = useSelector(authV2Selector);
  const enrolLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.invitationMfaEnrol]);
  const mfaVerifyLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.invitationMfaVerify]);

  const verifyMfaCode = async (nextCode: string[] | string) => {
    const codeString = Array.isArray(nextCode) ? nextCode.join("") : nextCode;
    const result = await dispatch(
      authV2InvitationMfaVerify({
        code: codeString.trim(),
        challengeId: enrolment?.challengeId ?? "",
        token,
      }),
    );
    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);
      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }
    const email = typeof result.payload?.email === "string" ? result.payload.email : "";
    const signInParams = new URLSearchParams();
    if (email) signInParams.set("email", email);
    navigate(`/auth/sign-in?${signInParams.toString()}`);
  };

  useEffect(() => {
    const enrol = async () => {
      if (!token) return;
      const result = await dispatch(authV2InvitationMfaEnrol({ token }));
      if (result.error) {
        return;
      }
      setEnrolment(result.payload);
    };

    enrol();
  }, [token, dispatch]);

  const formik = useFormik({
    initialValues: {
      code: Array(6).fill("") as string[],
    },
    onSubmit: (values) => {
      verifyMfaCode(values.code);
    },
    validationSchema: yup.object({
      code: yup
        .array()
        .of(yup.string())
        .min(6)
        .test(
          "all-filled",
          "Please enter the full 6-digit code.",
          (value) =>
            Array.isArray(value) && value.length === 6 && value.every((v) => typeof v === "string" && v.trim() !== ""),
        ),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  return (
    <>
      <AuthCard onSubmit={() => formik.handleSubmit()}>
        <Stack rowGap={6}>
          <Stack rowGap={3}>
            <Typography level="body-lg" bold>
              Scan the QR code
            </Typography>
            <Typography level="body-lg">
              Use an authenticator app like{" "}
              <Link href="https://1password.com/" target="_blank" rel="noreferrer">
                1Password
              </Link>
              ,{" "}
              <Link
                href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                target="_blank"
                rel="noreferrer"
              >
                Google Authenticator
              </Link>
              ,{" "}
              <Link href="https://authy.com/" target="_blank" rel="noreferrer">
                Authy
              </Link>
              , or{" "}
              <Link
                href="https://www.microsoft.com/en-gb/security/mobile-authenticator-app"
                target="_blank"
                rel="noreferrer"
              >
                Microsoft Authenticator
              </Link>{" "}
              to scan the QR code below.
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" columnGap={5}>
            <QRCodeWrapper variant="outlined" borderRadius="md" padding={4}>
              {enrolLoading || !enrolment?.totp ? (
                <CircularProgress size="md" />
              ) : (
                <QRCodeImage src={enrolment.totp.qrCode} alt="Scan this QR code with your authenticator app" />
              )}
            </QRCodeWrapper>
            <Stack direction="column" rowGap={2}>
              <Typography>Can't scan the code?</Typography>
              <Button variant="secondary" size="sm" onClick={() => setShowSetupKey(true)}>
                View the setup key
              </Button>
            </Stack>
          </Stack>
          <Divider>Then</Divider>
          <FormControl error={!!formik.errors.code}>
            <PinInput
              intent={formik.errors.code ? "danger" : undefined}
              length={6}
              onChange={(value) => {
                formik.setFieldValue("code", value);
                if (formik.errors.code) {
                  formik.setFieldError("code", undefined);
                }
              }}
              onComplete={(value) => {
                formik.setFieldValue("code", value.split(""));
                if (formik.errors.code) {
                  formik.setFieldError("code", undefined);
                }
                verifyMfaCode(value);
              }}
              autoFocus
              size="lg"
              value={formik.values.code}
              gap={2.5}
            />
            {formik.errors.code ? (
              <FormHelperText>
                <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                {formik.errors.code}
              </FormHelperText>
            ) : null}
          </FormControl>
          <Button
            size="lg"
            variant="primary"
            loading={mfaVerifyLoading}
            endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
            type="submit"
          >
            Verify
          </Button>
        </Stack>
      </AuthCard>
      <Modal open={showSetupKey} onClose={() => setShowSetupKey(false)}>
        <ModalDialog minWidth="600px">
          <ModalClose />
          <Typography level="title-md">Your setup key</Typography>
          <Typography level="body-lg">{enrolment?.totp?.secret}</Typography>
        </ModalDialog>
      </Modal>
    </>
  );
};

export { AuthInviteSetMfa };
